import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { deltaGoodness } from "@/lib/utils/daylight-insight/compute";
import { SHOW_BENCHMARK_LABEL } from "@/lib/utils/daylight-insight/constants";
import { fmtVal, formatAbsDelta } from "@/lib/utils/daylight-insight/format";
import type { DashboardMetric } from "./api/types";
import { apiToMetric, deltaOf, sparkPoints } from "./api/metricsAdapt";

/** Sparkline drawn straight from an API trend series (last 6 points). Coloured
 *  by trend goodness — green when the metric is moving the good way, red the bad
 *  way, blue when flat/undetermined — since the SP gives no numeric benchmark to
 *  grade current-vs-target on. Matches the card's delta-arrow colour. */
function ApiSpark({ points, state, wide }: { points: number[]; state: "good" | "bad" | "neutral"; wide?: boolean }) {
  const vals = points.slice(-6);
  if (vals.length < 2) return null;
  const min = Math.min(...vals);
  // The 1-floor keeps a flat near-zero series from filling the sparkline, but a
  // genuinely negative one (a debit metric) must scale to its own top value —
  // otherwise every bar is measured against a ceiling the data never reaches.
  const max = min < 0 ? Math.max(...vals) : Math.max(...vals, 1);
  const span = max - min || 1;
  const n = vals.length;
  const W = wide ? 120 : 62;
  const H = wide ? 22 : 26;
  const slot = W / n;
  const bw = slot * 0.6;
  const color = state === "good" ? "var(--green)" : state === "bad" ? "var(--red)" : "var(--chart-primary)";
  return (
    <svg width={wide ? "100%" : 62} height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio={wide ? "none" : "xMidYMid meet"} style={{ display: "block" }}>
      {vals.map((v, i) => {
        const h = 3 + ((v - min) / span) * (H - 5);
        return <rect key={i} x={i * slot + (slot - bw) / 2} y={H - h} width={bw} height={h} rx="1.5" fill={color} opacity={i === n - 1 ? 1 : 0.28} />;
      })}
    </svg>
  );
}

/** KPI tile backed by a live API metric. Mirrors {@link FlowCard} but reads the
 *  value/delta straight from the SP (no seed math), and shows the sparkline only
 *  when the metric is flagged `isKeyMetric`. Clicking it opens the Trends View. */
export function ApiFlowCard({
  metric,
  onSelect,
  selected = false,
}: {
  metric: DashboardMetric;
  onSelect?: (m: DashboardMetric) => void;
  /** The metric the open drill is showing — outlined so the tile it came from
   *  stays identifiable beside the slide-over. */
  selected?: boolean;
}) {
  const m = apiToMetric(metric);
  const delta = deltaOf(metric.currentValue, metric.priorValue);
  const dg = metric.currentValue === null || metric.priorValue === null ? "neutral" : deltaGoodness(m, delta.pct);
  return (
    <div className={"dl-card" + (selected ? " sel" : "")} style={onSelect ? { cursor: "pointer" } : undefined} onClick={() => onSelect?.(metric)}>
      <div className="dl-cname">{metric.name}</div>
      <div className="dl-cval num">{metric.currentValue === null ? "—" : fmtVal(m, metric.currentValue)}</div>
      <div className="dl-cfoot">
        <span className={"dl-delta num " + dg}>
          {dg === "good" ? <ArrowUpRight size={13} /> : dg === "bad" ? <ArrowDownRight size={13} /> : <Minus size={12} />}
          {metric.priorValue === null ? "—" : formatAbsDelta(m, delta)}
        </span>
        {SHOW_BENCHMARK_LABEL && metric.benchmarkLabel && <span className="dl-bench bench-neutral">{metric.benchmarkLabel}</span>}
      </div>
      {metric.isKeyMetric && <div className="dl-spark"><ApiSpark points={sparkPoints(metric)} state={dg} wide /></div>}
    </div>
  );
}

/** Snapshot ("Current State") tile backed by a live API metric. Mirrors the
 *  seeded {@link SnapCard}'s markup so the grid looks unchanged — only the data
 *  source differs. The SP sends no numeric target for these rows, so the status
 *  dot and the footer read the period-over-period move (same rule as the KPI
 *  cards) instead of a benchmark-breach state. */
export function ApiSnapCard({
  metric,
  onSelect,
  selected = false,
}: {
  metric: DashboardMetric;
  onSelect?: (m: DashboardMetric) => void;
  /** The metric the open drill is showing — outlined so the tile it came from
   *  stays identifiable beside the slide-over. */
  selected?: boolean;
}) {
  const m = apiToMetric(metric);
  const delta = deltaOf(metric.currentValue, metric.priorValue);
  const dg = metric.currentValue === null || metric.priorValue === null ? "neutral" : deltaGoodness(m, delta.pct);
  return (
    <div
      className={"dl-card snap" + (selected ? " sel" : "")}
      style={onSelect ? undefined : { cursor: "default" }}
      onClick={() => onSelect?.(metric)}
    >
      <div className="dl-cname"><span className={"snap-dot " + dg} />{metric.name}</div>
      <div className="dl-cval num">{metric.currentValue === null ? "—" : fmtVal(m, metric.currentValue)}</div>
      <div className="snap-foot">
        {SHOW_BENCHMARK_LABEL && metric.benchmarkLabel && <span className="snap-target">Target {metric.benchmarkLabel}</span>}
        {metric.priorValue !== null && Math.abs(delta.pct) >= 0.05 && (
          <span className="snap-trend">
            {delta.abs > 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
            {formatAbsDelta(m, delta)}
          </span>
        )}
      </div>
    </div>
  );
}

/** "More metrics" row backed by a live API metric. Mirrors {@link CompactMetric}.
 *  Clicking it opens the Trends View. */
export function ApiCompactMetric({
  metric,
  onSelect,
  selected = false,
}: {
  metric: DashboardMetric;
  onSelect?: (m: DashboardMetric) => void;
  /** The metric the open drill is showing — outlined so the tile it came from
   *  stays identifiable beside the slide-over. */
  selected?: boolean;
}) {
  const m = apiToMetric(metric);
  const delta = deltaOf(metric.currentValue, metric.priorValue);
  const dg = metric.currentValue === null || metric.priorValue === null ? "neutral" : deltaGoodness(m, delta.pct);
  return (
    <button className={"cmpt" + (selected ? " sel" : "")} onClick={() => onSelect?.(metric)}>
      <span className="cmpt-name">{metric.name}</span>
      <span className="cmpt-right">
        <span className="cmpt-val num">{metric.currentValue === null ? "—" : fmtVal(m, metric.currentValue)}</span>
        {metric.priorValue !== null && Math.abs(delta.pct) >= 0.05 && (
          <span className={"cmpt-delta num " + dg}>
            {dg === "good" ? <ArrowUpRight size={11} /> : dg === "bad" ? <ArrowDownRight size={11} /> : null}
            {Math.abs(Math.round(delta.pct))}%
          </span>
        )}
      </span>
    </button>
  );
}
